import React, { useState, useEffect } from 'react';
import { useSEO } from '../hooks/useSEO';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactGA from "react-ga4";

const ContactUs = () => {
  useSEO({
    title: "Contact AM CARDMATE - Get Support & Information",
    description: "Contact our team for support, questions, or partnership opportunities. We're here to help with your digital business card needs!",
    image: "/assets/contactus.jpg",
    keywords: "contact us, customer support, help, business inquiries, partnership, get in touch"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact AM CARDMATE",
        "url": "https://amcardmate.com/contact",
        "mainEntity": {
          "@type": "Organization",
          "name": "AM CARDMATE",
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "telephone": "+44-773-255-0488",
              "contactType": "customer service",
              "email": "support@amcardmate.com",
              "areaServed": "Worldwide",
              "availableLanguage": ["English", "Portuguese", "Russian", "Ukrainian"]
            }
          ]
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "AM CARDMATE",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Preston",
          "addressCountry": "GB"
        },
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      }
    ];
    
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: '',
    loading: false
  });

  const [focusedField, setFocusedField] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState('standard');
  const [showChatPopup, setShowChatPopup] = useState(false);
  const [chatFormData, setChatFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [chatFormStatus, setChatFormStatus] = useState({
    loading: false,
    success: false,
    error: false,
    message: ''
  });

  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mvgrqolv';
  
  useEffect(() => {
    const removeFormbuttons = () => {
      const floatingButtons = document.querySelectorAll('[data-formbutton]');
      floatingButtons.forEach(btn => btn.remove());
      const containers = document.querySelectorAll('[class*="formbutton"]');
      containers.forEach(container => container.remove());
      const overlays = document.querySelectorAll('[class*="formbutton-overlay"]');
      overlays.forEach(overlay => overlay.remove());
      if (window.formbutton) {
        delete window.formbutton;
      }
    };
    removeFormbuttons();
    const interval = setInterval(removeFormbuttons, 1000);
    return () => {
      clearInterval(interval);
      removeFormbuttons();
    };
  }, []);

  const openChatPopup = () => {
    ReactGA.event({ category: "Support", action: "Open Chat Popup" });
    setShowChatPopup(true);
  };

  const closeChatPopup = () => {
    setShowChatPopup(false);
    setChatFormData({ name: '', email: '', message: '' });
    setChatFormStatus({ loading: false, success: false, error: false, message: '' });
  };

  const handleChatInputChange = (e) => {
    const { name, value } = e.target;
    setChatFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    ReactGA.event({ category: "Support", action: "Submit Chat Form" });
    setChatFormStatus({ loading: true, success: false, error: false, message: '' });
    
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: chatFormData.name,
          email: chatFormData.email,
          message: chatFormData.message,
          type: 'Quick Chat Support',
          _subject: `Quick Chat Support from ${chatFormData.name}`
        }),
      });
      
      if (response.ok) {
        ReactGA.event({ category: "Support", action: "Chat Form Success" });
        setChatFormStatus({
          loading: false,
          success: true,
          error: false,
          message: 'Message sent successfully! We\'ll get back to you soon.'
        });
        setTimeout(() => { closeChatPopup(); }, 3000);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      ReactGA.event({ category: "Support", action: "Chat Form Error" });
      setChatFormStatus({
        loading: false,
        success: false,
        error: true,
        message: 'Failed to send message. Please try again.'
      });
    }
  };

  const scrollToContactForm = () => {
    ReactGA.event({ category: "Support", action: "Scroll to Contact Form" });
    const formElement = document.getElementById('contact-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setFormStatus({ submitted: true, success: false, message: 'Please fill in all required fields.', loading: false });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus({ submitted: true, success: false, message: 'Please enter a valid email address.', loading: false });
      return;
    }
    
    ReactGA.event({ category: "Support", action: "Submit Contact Form", label: `${selectedPriority} - ${formData.subject}` });
    setFormStatus({ submitted: false, success: false, message: '', loading: true });
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('company', formData.company || 'Not provided');
      formDataToSend.append('phone', formData.phone || 'Not provided');
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('priority', selectedPriority);
      formDataToSend.append('_replyto', formData.email);
      formDataToSend.append('_subject', `New ${selectedPriority} support request: ${formData.subject}`);
      
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formDataToSend,
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        ReactGA.event({ category: "Support", action: "Contact Form Success", label: selectedPriority });
        setFormStatus({ submitted: true, success: true, message: 'Thank you for reaching out! Your message has been sent successfully.', loading: false });
        
        setTimeout(() => {
          setFormData({ name: '', email: '', company: '', phone: '', subject: '', message: '' });
          setFormStatus({ submitted: false, success: false, message: '', loading: false });
          setSelectedPriority('standard');
        }, 5000);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      ReactGA.event({ category: "Support", action: "Contact Form Error" });
      console.error('Formspree Error:', error);
      setFormStatus({ submitted: true, success: false, message: 'Failed to send message. Please try again or contact us directly at support@amcardmate.com', loading: false });
    }
  };

  const contactMethods = [
    { id: 'email', icon: 'envelope', title: 'Email Support', subtitle: 'Detailed Help', description: 'Send us a detailed message and receive comprehensive assistance.', features: ['support@amcardmate.com', 'Response within 24h', 'Attach screenshots'], gradient: 'from-blue-500 to-indigo-600', action: 'Send Email', onClick: scrollToContactForm },
    { id: 'chat', icon: 'comments', title: 'Quick Chat Support', subtitle: 'Fast Assistance', description: 'Send us a quick message and get rapid responses from our support team.', features: ['Quick popup form', 'Fast responses', 'Easy to use'], gradient: 'from-green-500 to-emerald-600', action: 'Start Quick Chat', onClick: openChatPopup },
    { id: 'support', icon: 'headset', title: 'Priority Support', subtitle: 'For Premium Members', description: 'Get responses within 2-4 hours from our dedicated support team.', features: ['Direct expert assistance', 'Priority queue', 'Detailed solutions'], gradient: 'from-purple-500 to-pink-600', action: 'Get Priority Support', onClick: scrollToContactForm }
  ];

  const globalRegions = [
    { name: 'North America', countries: '12', icon: '🇺🇸' },
    { name: 'Europe', countries: '18', icon: '🇬🇧' },
    { name: 'Asia Pacific', countries: '15', icon: '🇯🇵' },
    { name: 'Latin America', countries: '8', icon: '🇧🇷' },
    { name: 'Middle East', countries: '6', icon: '🇦🇪' },
    { name: 'Africa', countries: '5', icon: '🇿🇦' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-blue-50/10 to-white">
      <style>{`[data-formbutton],[class*="formbutton"],.formbutton-overlay,.formbutton-button{display:none!important;visibility:hidden!important;}`}</style>

      {/* Hero Section */}
      <section className="pt-20 md:pt-24 lg:pt-32 pb-20 bg-gradient-to-b from-white via-blue-50/20 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-200/20 via-purple-200/20 to-transparent rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-purple-200/20 via-pink-200/20 to-transparent rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="badge badge-blue inline-flex mb-6">
              <FontAwesomeIcon icon="envelope" />
              <span>Get in Touch</span>
            </div>
            
            <h1 className="text-h1 mb-6">
              We're Here to <span className="text-gradient-blue-pink">Help You</span>
            </h1>
            
            <p className="text-body-lg text-gray max-w-3xl mx-auto mb-8">
              Our global support team is ready to assist you 24/7. Experience world-class support 
              that helps you make the most of AM CARDMATE. Use our quick chat for fast responses!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={scrollToContactForm} className="btn btn-primary group">
                <FontAwesomeIcon icon="paper-plane" />
                <span>Send Message</span>
              </button>
              <button onClick={openChatPopup} className="btn btn-outline group">
                <FontAwesomeIcon icon="comments" />
                <span>Quick Chat Support</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Support Channels Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="badge badge-purple inline-flex mb-6">
                <FontAwesomeIcon icon="headset" />
                <span>Support Channels</span>
              </div>
              <h2 className="text-h2 mb-6">Choose How to <span className="text-gradient-blue-pink">Connect</span></h2>
              <p className="text-body-lg text-gray">Multiple channels, one commitment: exceptional support</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {contactMethods.map((method) => (
                <div key={method.id} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group">
                  <div className={`w-14 h-14 bg-gradient-to-br ${method.gradient} rounded-xl flex items-center justify-center text-white mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                    <FontAwesomeIcon icon={method.icon} className="text-xl" />
                  </div>
                  <h3 className="text-card-title text-dark mb-1">{method.title}</h3>
                  <p className="text-body-sm text-gray-600 mb-3">{method.subtitle}</p>
                  <p className="text-card-body mb-4">{method.description}</p>
                  <ul className="space-y-2 mb-6">
                    {method.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-body-sm text-gray-600">
                        <FontAwesomeIcon icon="check-circle" className="text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={method.onClick} className={`w-full btn bg-gradient-to-r ${method.gradient} text-white hover:opacity-90 transition-opacity`}>
                    {method.action}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-16 md:py-24 bg-gradient-to-b from-blue-50/30 via-white to-purple-50/20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="badge badge-blue inline-flex mb-6">
                <FontAwesomeIcon icon="paper-plane" />
                <span>Contact Form</span>
              </div>
              <h2 className="text-h2 mb-6">Send Us a <span className="text-gradient-blue-pink">Message</span></h2>
              <p className="text-body-lg text-gray">Fill out the form below and we'll get back to you promptly</p>
            </div>
            
            <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 lg:p-10">
              {/* Priority Selection */}
              <div className="mb-8">
                <label className="block text-body font-semibold text-gray-700 mb-4">Select Support Priority</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className={`relative flex items-center p-5 rounded-2xl cursor-pointer transition-all ${selectedPriority === 'standard' ? 'bg-gradient-to-r from-blue-50 to-purple-50 shadow-lg transform scale-[1.02]' : 'bg-gray-50 hover:bg-gray-100 shadow-sm hover:shadow-md'}`}>
                    <input type="radio" name="priority" value="standard" checked={selectedPriority === 'standard'} onChange={(e) => { setSelectedPriority(e.target.value); ReactGA.event({ category: "Support", action: "Select Priority", label: "Standard" }); }} className="sr-only" />
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md transition-all ${selectedPriority === 'standard' ? 'bg-gradient-to-br from-blue-400 to-blue-600 text-white' : 'bg-white text-blue-500'}`}>
                        <FontAwesomeIcon icon="clock" className="text-lg" />
                      </div>
                      <div>
                        <h4 className="text-card-title text-dark">Standard Support</h4>
                        <p className="text-body-sm text-gray-600">Response within 24 hours</p>
                      </div>
                    </div>
                  </label>
                  
                  <label className={`relative flex items-center p-5 rounded-2xl cursor-pointer transition-all ${selectedPriority === 'priority' ? 'bg-gradient-to-r from-purple-50 to-pink-50 shadow-lg transform scale-[1.02]' : 'bg-gray-50 hover:bg-gray-100 shadow-sm hover:shadow-md'}`}>
                    <input type="radio" name="priority" value="priority" checked={selectedPriority === 'priority'} onChange={(e) => { setSelectedPriority(e.target.value); ReactGA.event({ category: "Support", action: "Select Priority", label: "Priority" }); }} className="sr-only" />
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md transition-all ${selectedPriority === 'priority' ? 'bg-gradient-to-br from-purple-400 to-pink-600 text-white' : 'bg-white text-purple-500'}`}>
                        <FontAwesomeIcon icon="bolt" className="text-lg" />
                      </div>
                      <div>
                        <h4 className="text-card-title text-dark">Priority Support</h4>
                        <p className="text-body-sm text-gray-600">Response within 2-4 hours</p>
                      </div>
                    </div>
                    <span className="absolute top-2 right-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white text-caption px-2 py-1 rounded-full font-semibold">Premium</span>
                  </label>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-body-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                    <div className="relative">
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} onFocus={() => setFocusedField('name')} onBlur={() => setFocusedField(null)} className={`w-full px-4 py-3 rounded-xl bg-gray-50 transition-all text-body ${focusedField === 'name' ? 'bg-white shadow-lg shadow-blue-500/10 transform scale-[1.02]' : 'hover:bg-gray-100'} focus:outline-none`} placeholder="John Doe" required disabled={formStatus.loading} />
                      <FontAwesomeIcon icon="user" className={`absolute right-3 top-3.5 transition-colors ${focusedField === 'name' ? 'text-blue-500' : 'text-gray-400'}`} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-body-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <div className="relative">
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)} className={`w-full px-4 py-3 rounded-xl bg-gray-50 transition-all text-body ${focusedField === 'email' ? 'bg-white shadow-lg shadow-blue-500/10 transform scale-[1.02]' : 'hover:bg-gray-100'} focus:outline-none`} placeholder="john@example.com" required disabled={formStatus.loading} />
                      <FontAwesomeIcon icon="envelope" className={`absolute right-3 top-3.5 transition-colors ${focusedField === 'email' ? 'text-blue-500' : 'text-gray-400'}`} />
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="company" className="block text-body-sm font-semibold text-gray-700 mb-2">Company (Optional)</label>
                    <div className="relative">
                      <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} onFocus={() => setFocusedField('company')} onBlur={() => setFocusedField(null)} className={`w-full px-4 py-3 rounded-xl bg-gray-50 transition-all text-body ${focusedField === 'company' ? 'bg-white shadow-lg shadow-blue-500/10 transform scale-[1.02]' : 'hover:bg-gray-100'} focus:outline-none`} placeholder="Your Company" disabled={formStatus.loading} />
                      <FontAwesomeIcon icon="building" className={`absolute right-3 top-3.5 transition-colors ${focusedField === 'company' ? 'text-blue-500' : 'text-gray-400'}`} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-body-sm font-semibold text-gray-700 mb-2">Phone Number (Optional)</label>
                    <div className="relative">
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} onFocus={() => setFocusedField('phone')} onBlur={() => setFocusedField(null)} className={`w-full px-4 py-3 rounded-xl bg-gray-50 transition-all text-body ${focusedField === 'phone' ? 'bg-white shadow-lg shadow-blue-500/10 transform scale-[1.02]' : 'hover:bg-gray-100'} focus:outline-none`} placeholder="+44 123 456 7890" disabled={formStatus.loading} />
                      <FontAwesomeIcon icon="phone" className={`absolute right-3 top-3.5 transition-colors ${focusedField === 'phone' ? 'text-blue-500' : 'text-gray-400'}`} />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-body-sm font-semibold text-gray-700 mb-2">Subject *</label>
                  <div className="relative">
                    <select id="subject" name="subject" value={formData.subject} onChange={handleChange} onFocus={() => setFocusedField('subject')} onBlur={() => setFocusedField(null)} className={`w-full px-4 py-3 rounded-xl bg-gray-50 appearance-none cursor-pointer transition-all text-body ${focusedField === 'subject' ? 'bg-white shadow-lg shadow-blue-500/10 transform scale-[1.02]' : 'hover:bg-gray-100'} focus:outline-none`} required disabled={formStatus.loading}>
                      <option value="">Select a topic</option>
                      <option value="general">General Inquiry</option>
                      <option value="support">Technical Support</option>
                      <option value="business">Business Solutions</option>
                      <option value="partnership">Partnership Opportunities</option>
                      <option value="custom">Custom Development</option>
                      <option value="feedback">Product Feedback</option>
                      <option value="billing">Billing & Payments</option>
                    </select>
                    <FontAwesomeIcon icon="chevron-down" className={`absolute right-3 top-3.5 transition-colors pointer-events-none ${focusedField === 'subject' ? 'text-blue-500' : 'text-gray-400'}`} />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-body-sm font-semibold text-gray-700 mb-2">Your Message *</label>
                  <div className="relative">
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} onFocus={() => setFocusedField('message')} onBlur={() => setFocusedField(null)} rows="6" className={`w-full px-4 py-3 rounded-xl bg-gray-50 resize-none transition-all text-body ${focusedField === 'message' ? 'bg-white shadow-lg shadow-blue-500/10 transform scale-[1.02]' : 'hover:bg-gray-100'} focus:outline-none`} placeholder="Please provide as much detail as possible about your inquiry..." required disabled={formStatus.loading}></textarea>
                    <div className="absolute bottom-3 right-3 text-caption text-gray-400">{formData.message.length}/500</div>
                  </div>
                  <p className="mt-2 text-caption text-gray-500">Please be as detailed as possible to help us assist you better.</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between pt-4">
                  <p className="text-body-sm text-gray-600 flex items-center gap-2">
                    <FontAwesomeIcon icon="lock" className="text-green-500" />
                    Your information is secure and protected
                  </p>
                  <button type="submit" className={`btn btn-primary px-8 group ${formStatus.loading ? 'opacity-75 cursor-not-allowed' : ''}`} disabled={formStatus.loading}>
                    {formStatus.loading ? (
                      <><FontAwesomeIcon icon="spinner" className="animate-spin" /><span>Sending...</span></>
                    ) : (
                      <><FontAwesomeIcon icon="paper-plane" /><span>Send Message</span><FontAwesomeIcon icon="arrow-right" className="ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" /></>
                    )}
                  </button>
                </div>
                
                <AnimatePresence>
                  {formStatus.submitted && (
                    <motion.div className={`p-5 rounded-2xl text-center ${formStatus.success ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700' : 'bg-gradient-to-r from-red-50 to-pink-50 text-red-700'}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                      <FontAwesomeIcon icon={formStatus.success ? "check-circle" : "exclamation-circle"} className="text-2xl mb-2" />
                      <p className="text-body font-semibold">{formStatus.message}</p>
                      <p className="text-body-sm mt-1">{formStatus.success ? `We'll respond to your ${selectedPriority === 'priority' ? 'priority' : 'standard'} support request soon.` : 'Please try again or contact us directly at support@amcardmate.com'}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Global Reach Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50/20 via-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="badge badge-green inline-flex mb-6">
                <FontAwesomeIcon icon="globe" />
                <span>Global Reach</span>
              </div>
              <h2 className="text-h2 mb-6">Supporting Teams <span className="text-gradient-blue-pink">Worldwide</span></h2>
              <p className="text-body-lg text-gray max-w-3xl mx-auto">Trusted by professionals in over 50 countries</p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-3xl shadow-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="p-8 lg:p-12">
                  <div className="grid grid-cols-2 gap-6 mb-10">
                    <div className="text-center p-4 bg-white/70 backdrop-blur-sm rounded-2xl hover:bg-white transition-all">
                      <div className="text-h3 text-gradient-blue-pink mb-1">50+</div>
                      <div className="text-body-sm text-gray-600">Countries</div>
                    </div>
                    <div className="text-center p-4 bg-white/70 backdrop-blur-sm rounded-2xl hover:bg-white transition-all">
                      <div className="text-h3 text-gradient-blue-pink mb-1">24/7</div>
                      <div className="text-body-sm text-gray-600">Support</div>
                    </div>
                    <div className="text-center p-4 bg-white/70 backdrop-blur-sm rounded-2xl hover:bg-white transition-all">
                      <div className="text-h3 text-gradient-blue-pink mb-1">99.9%</div>
                      <div className="text-body-sm text-gray-600">Uptime</div>
                    </div>
                    <div className="text-center p-4 bg-white/70 backdrop-blur-sm rounded-2xl hover:bg-white transition-all">
                      <div className="text-h3 text-gradient-blue-pink mb-1">Endless</div>
                      <div className="text-body-sm text-gray-600">Happy Users</div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-h5 text-dark mb-4">Our Global Presence</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {globalRegions.map((region, index) => (
                        <div key={index} className="bg-white/70 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all flex items-center gap-3 group">
                          <div className="text-2xl group-hover:scale-110 transition-transform">{region.icon}</div>
                          <div className="flex-1">
                            <h4 className="text-body-sm font-semibold text-dark">{region.name}</h4>
                            <p className="text-caption text-gray-600"><span className="text-body font-bold text-gradient-blue-pink">{region.countries}</span> countries</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="relative h-full min-h-[400px] lg:min-h-0">
                  <img src="/assets/image5.jpg" alt="Global Support Team" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent to-purple-900/20"></div>
                  <div className="absolute bottom-8 left-8 right-8 text-white">
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                      <h3 className="text-h5 mb-2">Always Here for You</h3>
                      <p className="text-body-sm text-white/90">Our dedicated support team spans across timezones to ensure you get help whenever you need it.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chat Popup Modal */}
      <AnimatePresence>
        {showChatPopup && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeChatPopup} />
            
            <motion.div className="relative bg-gradient-to-br from-white to-slate-50 rounded-3xl shadow-2xl p-8 w-full max-w-md border border-slate-200/50" initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ type: "spring", duration: 0.5 }}>
              <button onClick={closeChatPopup} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-800 transition-colors">
                <FontAwesomeIcon icon="times" />
              </button>
              
              <div className="text-center mb-6">
                <h3 className="text-h4 text-gradient-blue-pink mb-2">Quick Chat Support</h3>
                <p className="text-body text-slate-600">Need quick help? Send us a message and we'll get back to you right away!</p>
              </div>
              
              {chatFormStatus.success && (
                <motion.div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-2xl text-green-700" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon="check-circle" className="text-green-500" />
                    <span className="text-body font-semibold">{chatFormStatus.message}</span>
                  </div>
                </motion.div>
              )}
              
              {chatFormStatus.error && (
                <motion.div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon="exclamation-circle" className="text-red-500" />
                    <span className="text-body font-semibold">{chatFormStatus.message}</span>
                  </div>
                </motion.div>
              )}
              
              <form onSubmit={handleChatSubmit} className="space-y-4">
                <div>
                  <label className="block text-body-sm font-semibold text-slate-700 mb-2">Name *</label>
                  <input type="text" name="name" value={chatFormData.name} onChange={handleChatInputChange} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:bg-white focus:outline-none transition-all text-body" placeholder="Your name" required disabled={chatFormStatus.loading} />
                </div>
                <div>
                  <label className="block text-body-sm font-semibold text-slate-700 mb-2">Email *</label>
                  <input type="email" name="email" value={chatFormData.email} onChange={handleChatInputChange} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:bg-white focus:outline-none transition-all text-body" placeholder="your@email.com" required disabled={chatFormStatus.loading} />
                </div>
                <div>
                  <label className="block text-body-sm font-semibold text-slate-700 mb-2">Quick Message *</label>
                  <textarea name="message" value={chatFormData.message} onChange={handleChatInputChange} rows="4" className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:bg-white focus:outline-none transition-all resize-none text-body" placeholder="How can we help you today?" required disabled={chatFormStatus.loading} />
                </div>
                <button type="submit" disabled={chatFormStatus.loading || chatFormStatus.success} className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white text-btn py-3 px-6 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100">
                  {chatFormStatus.loading ? (<><FontAwesomeIcon icon="spinner" className="animate-spin mr-2" />Sending...</>) : chatFormStatus.success ? (<><FontAwesomeIcon icon="check" className="mr-2" />Message Sent!</>) : (<><FontAwesomeIcon icon="paper-plane" className="mr-2" />Send Message</>)}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactUs;